// Sprint 157 (13/09/2026) — Token d'admin a TOTES les crides /api/admin/*.
//
// Fins avui el servidor no demanava autenticació a les rutes /api/admin/*:
// qualsevol podia descarregar-se de cop les fitxes de tots els titulars
// (adreça, referència cadastral, email, DNI). El panell d'admin funcionava
// perquè el servidor no ho comprovava, i per això ~60 crides del panell no
// duien capçalera Authorization.
//
// Ara el servidor les tanca en un sol punt (backend/server.py, _admin_guard),
// i aquest fitxer és l'altra meitat: afegeix el token a qualsevol crida a
// /api/admin/* de la NOSTRA API, per axios o per fetch, sense haver de tocar
// les 60 crides una a una.
//
// ⚠️ Només s'afegeix a URL del backend propi (REACT_APP_BACKEND_URL). Mai a
// un tercer: seria regalar la sessió d'admin a qui fos a l'altra banda.

import axios from "axios";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

function apiOrigin() {
  try {
    return API_BASE ? new URL(API_BASE).origin : window.location.origin;
  } catch {
    return window.location.origin;
  }
}

// La sessió d'admin viu en UNA sola clau: sessionStorage["adminToken"], que
// escriu només AdminLogin.js. Fins al 09/10/2026 aquí hi havia una segona
// lectura de localStorage["admin_token"], i tres components la feien servir
// com si fos la bona; però cap línia del projecte no l'havia escrit mai, o
// sigui que sempre valia null. Aquells tres llocs anaven bé per accident:
// passaven un objecte de capçaleres buit i l'interceptor d'aquí baix els hi
// posava el token de debò. Si algú els hagués "arreglat" posant-hi un
// `Bearer ${token}` directe, haurien enviat el text «Bearer null» i
// l'interceptor no l'hauria sobreescrit (només omple si no n'hi ha cap).
//
// Per tant: ni localStorage ni cap altre nom. Si cal el token, és aquí.
export function getAdminToken() {
  try {
    const tok = sessionStorage.getItem("adminToken");
    const exp = sessionStorage.getItem("adminTokenExpires");
    return tok && (!exp || new Date(exp) > new Date()) ? tok : null;
  } catch {
    return null;
  }
}

function ownPath(url) {
  if (!url) return null;
  let u;
  try {
    u = new URL(String(url), window.location.origin);
  } catch {
    return null;
  }
  if (u.origin !== apiOrigin() && u.origin !== window.location.origin) return null;
  return u.pathname.replace(/\/{2,}/g, "/");
}

export function isAdminApiUrl(url) {
  const path = ownPath(url);
  return !!path && (path.startsWith("/api/admin/") || path === "/api/admin");
}

// ── Sprint 157 — token del TITULAR d'una fitxa ────────────────────────────
// El backend ja no dona adreça, cadastre, email ni telèfon a qui no s'ha
// identificat. En validar la contrasenya de la fitxa, el titular rep un token
// (2 h) que es guarda per pestanya a sessionStorage i s'envia només a les
// crides d'AQUELLA fitxa (/api/pools/{id}...) i dels seus components.
const OWNER_KEY = (id) => `plxOwner:${String(id).toUpperCase()}`;

function jwtExp(tok) {
  try {
    const p = JSON.parse(atob(tok.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof p.exp === "number" ? p.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function setOwnerToken(poollexId, token) {
  if (!poollexId || !token) return;
  try {
    sessionStorage.setItem(OWNER_KEY(poollexId), token);
    sessionStorage.setItem("plxOwner:last", token);
  } catch { /* navegador sense storage: només durarà aquesta vista */ }
}

export function getOwnerToken(poollexId) {
  try {
    const tok = poollexId ? sessionStorage.getItem(OWNER_KEY(poollexId))
                          : sessionStorage.getItem("plxOwner:last");
    if (!tok) return null;
    const exp = jwtExp(tok);
    return exp && exp > Date.now() ? tok : null;
  } catch {
    return null;
  }
}

function tokenFor(url) {
  const path = ownPath(url);
  if (!path) return null;
  // L'admin veu-ho tot: el seu token va a qualsevol crida a la nostra API.
  const admin = getAdminToken();
  if (admin && path.startsWith("/api/")) return admin;
  const m = path.match(/^\/api\/pools\/(PLX-[A-Z0-9-]+)(\/|$)/i);
  if (m) return getOwnerToken(m[1]);
  if (path.startsWith("/api/componentes/")) return getOwnerToken(null);
  // mòdul antic de passaports: el propietari és el client identificat
  if (path.startsWith("/api/passport/")) {
    try { return localStorage.getItem("client_token"); } catch { return null; }
  }
  return null;
}

// Sprint 157 — Descàrrega amb credencial. Un <a href> o un window.open() no
// poden enviar capçaleres: el PDF que obrien era el PÚBLIC, sense adreça ni
// cadastre. Passant per axios, l'interceptor hi posa el token del titular o
// de l'admin i el backend torna el complet.
export async function downloadApiFile(url, filename) {
  const res = await axios.get(url, { responseType: "blob" });
  const blobUrl = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename || "document.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
}

let installed = false;

export function installAdminAuth() {
  if (installed) return;
  installed = true;

  axios.interceptors.request.use((config) => {
    const url = config.baseURL && !/^https?:/i.test(config.url || "")
      ? `${config.baseURL.replace(/\/+$/, "")}/${(config.url || "").replace(/^\/+/, "")}`
      : config.url;
    const tok = tokenFor(url);
    if (tok) {
      config.headers = config.headers || {};
      const has = config.headers.Authorization || config.headers.authorization;
      if (!has) config.headers.Authorization = `Bearer ${tok}`;
    }
    return config;
  });

  if (typeof window !== "undefined" && typeof window.fetch === "function") {
    const original = window.fetch.bind(window);
    window.fetch = (input, init = {}) => {
      const url = typeof input === "string" ? input : input && input.url;
      const tok = tokenFor(url);
      if (!tok) return original(input, init);
      const headers = new Headers(
        init.headers || (typeof input !== "string" && input ? input.headers : undefined)
      );
      if (!headers.has("Authorization")) headers.set("Authorization", `Bearer ${tok}`);
      return original(input, { ...init, headers });
    };
  }
}
